'use client';

import { useEffect, useMemo, useRef, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import { ColumnDirective, ColumnsDirective, GridComponent, Inject, Resize } from '@syncfusion/ej2-react-grids';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  DialogTitle,
  FormControl,
  IconButton,
  InputLabel,
  Link as MuiLink,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
  useMediaQuery,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import { FileCode2, FileQuestion, FileText, X } from 'lucide-react';
import { useAppColors } from '@/styles/colors';
import {
  canonicalizeInstrumentReportType,
  collectReportTypes,
  sortInstrumentReports,
  type InstrumentReportSort,
  withReportDateRange,
} from '@/libs/ticker-report-display';

const GRID_RADIUS = 1;
const portfolioExposureFormatter = new Intl.NumberFormat('pt-BR', {
  style: 'percent',
  minimumFractionDigits: 1,
  maximumFractionDigits: 1,
});

type FiiSummary = {
  proventos?: string | null;
  proventosSentimento?: string | null;
  prospectSubscricao?: string | null;
  prospectSubscricaoSentimento?: string | null;
  mudancasVacanciaInadimplencia?: string | null;
  mudancasVacanciaInadimplenciaSentimento?: string | null;
};

type ReportSummary = {
  resumo?: string | null;
  sentimentoGeral?: string | null;
  fiiSummary?: FiiSummary | null;
};

export type InstrumentReportRow = {
  id: string;
  deliveryDate: string | null;
  documentType?: string | null;
  category?: string | null;
  documentDescription?: string | null;
  reportUrl?: string | null;
  reportXmlUrl?: string | null;
  contentType?: string | null;
  summary?: ReportSummary | null;
  assetCode?: string | null;
  assetName?: string | null;
  portfolioExposure?: number | null;
};

type ReportGridRow = InstrumentReportRow & {
  deliveryDateLabel: string;
  documentTypeLabel: string;
  documentDescriptionLabel: string;
};

type IndicatorDefinition = {
  id: string;
  label: string;
  valueKey?: keyof FiiSummary;
  sentimentKey?: keyof FiiSummary;
  source?: 'summary' | 'fii';
  sentimentSource?: 'summary' | 'fii';
  isSentiment?: boolean;
};

const INDICATORS: IndicatorDefinition[] = [
  {
    id: 'resumo_geral',
    label: 'Resumo geral',
    source: 'summary',
  },
  {
    id: 'sentimento_geral',
    label: 'Geral (sentimento)',
    sentimentSource: 'summary',
    isSentiment: true,
  },
  {
    id: 'proventos',
    label: 'Proventos',
    valueKey: 'proventos',
  },
  {
    id: 'proventos_sentimento',
    label: 'Proventos (sentimento)',
    sentimentKey: 'proventosSentimento',
    isSentiment: true,
  },
  {
    id: 'vacancia_inadimplencia',
    label: 'Vacância / inadimplência',
    valueKey: 'mudancasVacanciaInadimplencia',
  },
  {
    id: 'vacancia_inadimplencia_sentimento',
    label: 'Vacância / inadimplência (sentimento)',
    sentimentKey: 'mudancasVacanciaInadimplenciaSentimento',
    isSentiment: true,
  },
  {
    id: 'subscricao',
    label: 'Subscrição',
    valueKey: 'prospectSubscricao',
  },
  {
    id: 'subscricao_sentimento',
    label: 'Subscrição (sentimento)',
    sentimentKey: 'prospectSubscricaoSentimento',
    isSentiment: true,
  },
];

const PRESETS: Record<string, string[]> = {
  resumo: ['resumo_geral', 'sentimento_geral'],
  nenhum: [],
  papel: [
    'resumo_geral',
    'sentimento_geral',
    'proventos',
    'proventos_sentimento',
    'subscricao',
    'subscricao_sentimento',
  ],
  tijolo: [
    'resumo_geral',
    'sentimento_geral',
    'proventos',
    'proventos_sentimento',
    'vacancia_inadimplencia',
    'vacancia_inadimplencia_sentimento',
  ],
  hibrido: [
    'resumo_geral',
    'sentimento_geral',
    'proventos',
    'proventos_sentimento',
    'vacancia_inadimplencia',
    'vacancia_inadimplencia_sentimento',
  ],
  geral: [
    'resumo_geral',
    'sentimento_geral',
    'proventos',
    'proventos_sentimento',
    'vacancia_inadimplencia',
    'vacancia_inadimplencia_sentimento',
  ],
  todos_sentimentos: INDICATORS.filter((indicator) => indicator.isSentiment).map((indicator) => indicator.id),
  todos: INDICATORS.map((indicator) => indicator.id),
};

const PRESET_ALIASES: Record<string, keyof typeof PRESETS> = {
  hibrido: 'tijolo',
  geral: 'tijolo',
};

const PRESET_ORDER: Array<keyof typeof PRESETS> = ['resumo', 'nenhum', 'papel', 'tijolo', 'todos_sentimentos', 'todos'];

const PRESET_LABELS: Record<string, string> = {
  resumo: 'Resumo geral',
  nenhum: 'Nenhum',
  papel: 'Papel',
  tijolo: 'Tijolo',
  todos_sentimentos: 'Todos os sentimentos',
  todos: 'Todos os indicadores',
  custom: 'Customizado',
};

const INDICATOR_GROUPS = [
  {
    id: 'resumo',
    label: 'Resumo',
    items: ['resumo_geral', 'sentimento_geral'],
  },
  {
    id: 'receitas',
    label: 'Receitas',
    items: ['proventos', 'proventos_sentimento'],
  },
  {
    id: 'risco',
    label: 'Risco',
    items: ['vacancia_inadimplencia', 'vacancia_inadimplencia_sentimento', 'subscricao', 'subscricao_sentimento'],
  },
  {
    id: 'operacao',
    label: 'Operacao',
    items: [],
  },
];

type DownloadKind = 'pdf' | 'html' | 'file';

const getDownloadKind = (contentType?: string | null): DownloadKind => {
  const normalized = contentType?.toLowerCase().split(';', 1)[0].trim() ?? '';
  if (normalized.includes('pdf')) return 'pdf';
  if (normalized.includes('html')) return 'html';
  return 'file';
};

const getDownloadLabel = (kind: DownloadKind) => {
  if (kind === 'pdf') return 'PDF';
  if (kind === 'html') return 'HTML';
  return 'Arquivo';
};

const typeLabelWithDescription = (typeLabel: string, description: string) => {
  if (!typeLabel) return description === '-' ? 'Sem descrição' : description;
  return description === '-' ? typeLabel : `${typeLabel} - ${description}`;
};

const DownloadTypeIndicator = ({ kind }: { kind: DownloadKind }) => {
  const Icon = kind === 'pdf' ? FileText : kind === 'html' ? FileCode2 : FileQuestion;
  return (
    <Box
      component='span'
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        fontSize: 11,
        fontWeight: 700,
        letterSpacing: 0.3,
      }}
    >
      <Icon size={16} strokeWidth={2} aria-hidden='true' />
      {getDownloadLabel(kind)}
    </Box>
  );
};

const formatSentimentHeader = (label: string) => {
  return label.replace(/\s*\(sentimento\)\s*/i, ' Sent.');
};

const inferPresetFromIndicators = (indicators: string[]) => {
  const normalized = [...indicators].sort();
  const presetEntry = PRESET_ORDER.map((key) => [key, PRESETS[key]] as const).find(([, values]) => {
    const sortedValues = [...values].sort();
    return (
      sortedValues.length === normalized.length && sortedValues.every((value, index) => value === normalized[index])
    );
  });
  return presetEntry ? presetEntry[0] : 'custom';
};

const normalizePresetKey = (value: string) => PRESET_ALIASES[value] ?? value;

const sentimentTone = (value?: string | null) => {
  const normalized = (value ?? '').toLowerCase();
  if (normalized.includes('positivo')) return 'positive';
  if (normalized.includes('negativo')) return 'negative';
  if (normalized.includes('neutro')) return 'neutral';
  return 'missing';
};

const SentimentBadge = ({ value }: { value?: string | null }) => {
  const appColors = useAppColors();
  const tone = sentimentTone(value);
  const labelMap: Record<string, string> = {
    positive: 'Positivo',
    negative: 'Negativo',
    neutral: 'Neutro',
    missing: 'Ausente',
  };
  const title = labelMap[tone] ?? 'Ausente';
  const palette =
    tone === 'positive'
      ? {
          main: appColors.semantic.successStrong,
          dark: appColors.semantic.successStrong,
          light: appColors.semantic.success,
        }
      : tone === 'negative'
        ? {
            main: appColors.semantic.dangerStrong,
            dark: appColors.semantic.dangerStrong,
            light: appColors.semantic.danger,
          }
        : tone === 'neutral'
          ? {
              main: appColors.semantic.infoStrong,
              dark: appColors.semantic.infoStrong,
              light: appColors.semantic.info,
            }
          : {
              main: appColors.semantic.warningStrong,
              dark: appColors.semantic.warningStrong,
              light: appColors.semantic.warning,
            };

  if (tone === 'missing') {
    return (
      <Box
        component='span'
        title={title}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          borderRadius: 999,
          border: `1px solid ${palette.main}`,
          bgcolor: alpha(palette.light, 0.5),
          px: 1,
          py: 0.25,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 0.6,
          color: palette.dark,
        }}
      >
        -
      </Box>
    );
  }

  if (tone === 'neutral') {
    return (
      <Box
        component='span'
        title={title}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          borderRadius: 999,
          border: `1px solid ${palette.main}`,
          bgcolor: alpha(palette.light, 0.5),
          px: 1,
          py: 0.25,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 0.6,
          color: palette.dark,
        }}
      >
        =
      </Box>
    );
  }

  if (tone === 'positive') {
    return (
      <Box
        component='span'
        title={title}
        sx={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 0.5,
          borderRadius: 999,
          border: `1px solid ${palette.main}`,
          bgcolor: alpha(palette.light, 0.35),
          px: 1,
          py: 0.25,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: 0.6,
          color: palette.main,
          lineHeight: 1,
        }}
      >
        <Box component='span' aria-hidden>
          ▲
        </Box>
      </Box>
    );
  }

  return (
    <Box
      component='span'
      title={title}
      sx={{
        display: 'inline-flex',
        alignItems: 'center',
        gap: 0.5,
        borderRadius: 999,
        border: `1px solid ${palette.main}`,
        bgcolor: alpha(palette.light, 0.35),
        px: 1,
        py: 0.25,
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 0.6,
        color: palette.main,
        lineHeight: 1,
      }}
    >
      <Box component='span' aria-hidden>
        ▼
      </Box>
    </Box>
  );
};

export function TickerReportTable({
  reports,
  defaultPreset,
  fundTypeSymbol: _fundTypeSymbol,
  showAssetColumn = false,
  allowPortfolioSort = false,
  defaultSort = 'date_desc',
  showDateRange = false,
  initialStartDate = '',
  initialEndDate = '',
  availableReportTypes,
}: {
  reports: InstrumentReportRow[];
  defaultPreset: string;
  fundTypeSymbol: string;
  /** Render asset identity in consolidated portfolio report views. */
  showAssetColumn?: boolean;
  /** Let a consolidated portfolio view order documents by current holding exposure. */
  allowPortfolioSort?: boolean;
  defaultSort?: InstrumentReportSort;
  /** Reload the server-rendered report set for a user-selected date range. */
  showDateRange?: boolean;
  initialStartDate?: string;
  initialEndDate?: string;
  /** All document types available for the current assets, independent of dates. */
  availableReportTypes?: string[];
}) {
  const router = useRouter();
  const pathname = usePathname();
  const searchParams = useSearchParams();
  const theme = useTheme();
  const appColors = useAppColors();
  const isDesktop = useMediaQuery(theme.breakpoints.up('md'));
  const didInitFromUrl = useRef(false);
  const reportTypeTouched = useRef(false);
  const gridContainerRef = useRef<HTMLDivElement | null>(null);
  const gridRef = useRef<GridComponent | null>(null);
  const normalizedPresetKey = normalizePresetKey(defaultPreset);
  const normalizedPreset = PRESETS[normalizedPresetKey] ? normalizedPresetKey : 'tijolo';
  const tabParam = searchParams?.get('aba');
  const hasReportParams = Boolean(searchParams?.get('reportTypes')) || Boolean(searchParams?.get('indicators'));
  const isActive = tabParam === 'relatorios' || (!tabParam && hasReportParams);
  const suggestedPreset = normalizedPreset;
  const [preset, setPreset] = useState(normalizedPreset);
  const [selectedIndicators, setSelectedIndicators] = useState<string[]>(PRESETS[normalizedPreset] ?? []);
  const [reportTypeFilter, setReportTypeFilter] = useState<string[]>([]);
  const [reportSort, setReportSort] = useState<InstrumentReportSort>(defaultSort);
  const hideEmptyIndicators = true;
  const [isIndicatorsOpen, setIndicatorsOpen] = useState(false);
  const [isReportTypesOpen, setReportTypesOpen] = useState(false);
  const [gridHeight, setGridHeight] = useState<number | null>(null);
  const reportStartDate = searchParams?.get('reportStartDate') ?? initialStartDate;
  const reportEndDate = searchParams?.get('reportEndDate') ?? initialEndDate;

  const updateReportDate = (parameter: 'reportStartDate' | 'reportEndDate', value: string) => {
    if (!searchParams) return;
    let params = new URLSearchParams(searchParams.toString());
    if (showDateRange) {
      params = withReportDateRange(params, {
        startDate: initialStartDate,
        endDate: initialEndDate,
      });
    }
    if (value) {
      params.set(parameter, value);
    } else {
      params.delete(parameter);
    }
    router.replace(`${pathname}?${params.toString()}`, { scroll: false });
  };

  useEffect(() => {
    if (!isDesktop) {
      setGridHeight(null);
      return;
    }
    const updateHeight = () => {
      if (!gridContainerRef.current) return;
      const top = gridContainerRef.current.getBoundingClientRect().top;
      const footer = document.querySelector('footer');
      const footerHeight = footer instanceof HTMLElement ? footer.getBoundingClientRect().height : 0;
      const nextHeight = Math.max(240, window.innerHeight - top - footerHeight - 12);
      setGridHeight(nextHeight);
    };
    updateHeight();
    window.addEventListener('resize', updateHeight);
    return () => window.removeEventListener('resize', updateHeight);
  }, [isDesktop]);

  useEffect(() => {
    if (!isActive) return;
    if (!didInitFromUrl.current) return;
    if (!searchParams) return;
    const params = new URLSearchParams(searchParams.toString());

    if (reportTypeFilter.length > 0) {
      params.set('reportTypes', reportTypeFilter.join(','));
    } else {
      params.delete('reportTypes');
    }

    if (selectedIndicators.length > 0) {
      params.set('indicators', selectedIndicators.join(','));
    } else {
      params.delete('indicators');
    }

    const nextQuery = params.toString();
    const currentQuery = searchParams.toString();
    if (nextQuery !== currentQuery) {
      router.replace(`${pathname}?${nextQuery}`, { scroll: false });
    }
  }, [
    reportTypeFilter,
    selectedIndicators,
    pathname,
    router,
    searchParams,
    isActive,
    showDateRange,
    initialStartDate,
    initialEndDate,
  ]);

  useEffect(() => {
    if (preset === 'custom') return;
    setSelectedIndicators(PRESETS[preset] ?? []);
  }, [preset]);

  const selectedDefinitions = useMemo(
    () => INDICATORS.filter((indicator) => selectedIndicators.includes(indicator.id)),
    [selectedIndicators],
  );

  const indicatorById = useMemo(() => {
    return new Map(INDICATORS.map((indicator) => [indicator.id, indicator]));
  }, []);

  const groupedIndicators = useMemo(() => {
    const groupedIds = new Set<string>();
    const groups = INDICATOR_GROUPS.map((group) => {
      const items = group.items
        .map((id) => indicatorById.get(id))
        .filter((item): item is IndicatorDefinition => Boolean(item));
      items.forEach((item) => groupedIds.add(item.id));
      return { ...group, items };
    }).filter((group) => group.items.length > 0);
    const others = INDICATORS.filter((indicator) => !groupedIds.has(indicator.id));
    return { groups, others };
  }, [indicatorById]);

  const reportTypeOptions = useMemo(() => {
    return availableReportTypes ?? collectReportTypes(reports);
  }, [availableReportTypes, reports]);

  useEffect(() => {
    if (!isActive) return;
    if (didInitFromUrl.current) return;
    if (!searchParams) return;
    const reportTypesParam = searchParams.get('reportTypes') ?? '';
    const indicatorsParam = searchParams.get('indicators') ?? '';

    if (reportTypesParam) {
      const values = reportTypesParam
        .split(',')
        .map((value) => value.trim())
        .filter(Boolean);
      const normalizedOptions = reportTypeOptions.map((option) => option.toLowerCase());
      const filtered = values
        .map((value) => {
          const index = normalizedOptions.indexOf(value.toLowerCase());
          return index >= 0 ? reportTypeOptions[index] : value;
        })
        .filter(Boolean);
      if (filtered.length > 0) {
        setReportTypeFilter(filtered);
        reportTypeTouched.current = true;
      }
    }

    if (indicatorsParam) {
      const values = indicatorsParam.split(',').map((value) => value.trim());
      const filtered = values.filter((value) => INDICATORS.some((indicator) => indicator.id === value));
      if (filtered.length > 0) {
        setSelectedIndicators(filtered);
        setPreset(inferPresetFromIndicators(filtered));
        didInitFromUrl.current = true;
        return;
      }
    }

    setPreset(normalizedPreset);
    setSelectedIndicators(PRESETS[normalizedPreset] ?? []);
    didInitFromUrl.current = true;
  }, [normalizedPreset, reportTypeOptions, searchParams, isActive]);

  useEffect(() => {
    if (!isActive) return;
    if (reportTypeFilter.length > 0 || reportTypeOptions.length === 0) return;
    if (searchParams?.get('reportTypes')) return;
    if (reportTypeTouched.current) return;
    const normalize = (value: string) =>
      value
        .toLowerCase()
        .normalize('NFD')
        .replace(/[\u0300-\u036f]/g, '')
        .trim();
    const preferred = reportTypeOptions.filter((type) =>
      ['relatorio gerencial', 'fato relevante', 'noticia'].includes(normalize(type)),
    );
    if (preferred.length > 0) {
      setReportTypeFilter(preferred);
    }
  }, [reportTypeOptions, reportTypeFilter.length]);

  const enrichedReports = useMemo<ReportGridRow[]>(() => {
    return reports.map((report) => {
      const deliveryDate = report.deliveryDate
        ? new Intl.DateTimeFormat('pt-BR', { dateStyle: 'short' }).format(new Date(report.deliveryDate))
        : '-';
      return {
        ...report,
        deliveryDateLabel: deliveryDate,
        documentTypeLabel: canonicalizeInstrumentReportType(report.documentType ?? report.category ?? '-'),
        documentDescriptionLabel: report.documentDescription ?? '-',
      };
    });
  }, [reports]);

  const filteredReports = useMemo(() => {
    if (reportTypeFilter.length === 0) {
      return enrichedReports;
    }
    return enrichedReports.filter((report) => reportTypeFilter.includes(report.documentTypeLabel));
  }, [enrichedReports, reportTypeFilter]);

  const toggleReportType = (type: string) => {
    reportTypeTouched.current = true;
    setReportTypeFilter((current) => {
      if (current.includes(type)) {
        return current.filter((value) => value !== type);
      }
      return [...current, type];
    });
  };

  const renderIndicatorValue = (report: ReportGridRow, indicator: IndicatorDefinition) => {
    const fiiSummary = report.summary?.fiiSummary;
    const value =
      indicator.source === 'summary'
        ? (report.summary?.resumo ?? null)
        : indicator.valueKey
          ? fiiSummary?.[indicator.valueKey]
          : null;
    const sentiment =
      indicator.sentimentSource === 'summary'
        ? (report.summary?.sentimentoGeral ?? null)
        : indicator.sentimentKey
          ? fiiSummary?.[indicator.sentimentKey]
          : null;
    return indicator.isSentiment ? sentiment : value;
  };

  const sortedReports = useMemo(
    () => sortInstrumentReports(filteredReports, reportSort),
    [filteredReports, reportSort],
  );

  const indicatorDataMap = useMemo(() => {
    const map = new Map<string, boolean>();
    selectedDefinitions.forEach((indicator) => {
      const hasData = sortedReports.some((report) => {
        const value = renderIndicatorValue(report, indicator);
        if (value == null) return false;
        if (typeof value === 'string') return value.trim().length > 0;
        return true;
      });
      map.set(indicator.id, hasData);
    });
    return map;
  }, [selectedDefinitions, sortedReports]);

  const visibleDefinitions = useMemo(() => {
    if (!hideEmptyIndicators) return selectedDefinitions;
    return selectedDefinitions.filter((indicator) => indicatorDataMap.get(indicator.id));
  }, [hideEmptyIndicators, indicatorDataMap, selectedDefinitions]);

  const gridRows = useMemo(() => {
    return sortedReports.map((report) => {
      const row = { ...report } as ReportGridRow & Record<string, string>;
      visibleDefinitions.forEach((indicator) => {
        const fieldValue = renderIndicatorValue(report, indicator);
        row[`indicator_${indicator.id}`] =
          typeof fieldValue === 'string' ? fieldValue : fieldValue ? String(fieldValue) : '';
      });
      return row;
    });
  }, [sortedReports, visibleDefinitions]);

  const gridKey = useMemo(() => {
    const typesKey = reportTypeFilter.join(',');
    const indicatorsKey = visibleDefinitions.map((item) => item.id).join(',');
    return `grid-${typesKey}-${indicatorsKey}`;
  }, [reportTypeFilter, visibleDefinitions]);

  const baseColumns = useMemo(
    () => [
      {
        field: 'deliveryDateLabel',
        headerText: 'Data',
        width: 104,
        minWidth: 96,
        maxWidth: 120,
        headerTemplate: () => <span title='Data'>Data</span>,
      },
      ...(showAssetColumn
        ? [
            {
              field: 'assetCode',
              headerText: 'Ativo',
              width: 150,
              minWidth: 128,
              maxWidth: 180,
              clipMode: 'EllipsisWithTooltip',
              headerTemplate: () => <span title='Ativo'>Ativo</span>,
              template: (row: ReportGridRow) => {
                const assetCode = row.assetCode;
                if (!assetCode) {
                  return <Typography color='text.secondary'>-</Typography>;
                }
                return (
                  <Stack spacing={0.15} sx={{ py: 0.25 }}>
                    <MuiLink
                      href={`/ativo/${encodeURIComponent(assetCode)}`}
                      underline='hover'
                      fontWeight={700}
                      fontSize={12}
                      color={appColors.actions.accentOutline.text}
                    >
                      <Stack direction='row' alignItems='baseline' gap={0.75}>
                        <Box component='span'>{assetCode}</Box>
                        {row.portfolioExposure !== null && row.portfolioExposure !== undefined ? (
                          <Typography variant='caption' color='text.secondary' sx={{ fontSize: 10 }}>
                            {portfolioExposureFormatter.format(row.portfolioExposure / 100)} da carteira
                          </Typography>
                        ) : null}
                      </Stack>
                    </MuiLink>
                    {row.assetName ? (
                      <Typography
                        variant='caption'
                        color='text.secondary'
                        sx={{
                          lineHeight: 1.2,
                          fontSize: 10.5,
                          display: '-webkit-box',
                          WebkitBoxOrient: 'vertical',
                          WebkitLineClamp: 2,
                          overflow: 'hidden',
                        }}
                      >
                        {row.assetName}
                      </Typography>
                    ) : null}
                  </Stack>
                );
              },
            },
          ]
        : []),
      {
        field: 'reportView',
        headerText: 'Ver original',
        width: 145,
        minWidth: 124,
        maxWidth: 180,
        clipMode: 'EllipsisWithTooltip',
        headerTemplate: () => <span title='Ver original'>Ver original</span>,
        template: (row: ReportGridRow) => {
          const description = row.documentDescriptionLabel || '-';
          const typeLabel = row.documentTypeLabel || '';
          const label = typeLabel || 'Abrir';
          return row.reportUrl ? (
            <MuiLink
              href={row.reportUrl}
              target='_blank'
              rel='noreferrer'
              underline='hover'
              title={typeLabelWithDescription(typeLabel, description)}
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                color: appColors.actions.accentOutline.text,
                minWidth: 0,
                '&:hover': {
                  color: appColors.actions.accentOutline.hoverText,
                },
              }}
            >
              <Box
                component='span'
                sx={{
                  minWidth: 0,
                  overflow: 'hidden',
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: 1,
                  textOverflow: 'ellipsis',
                }}
              >
                {label}
              </Box>
            </MuiLink>
          ) : (
            <Typography variant='body2' color='text.secondary' title={typeLabel}>
              Sem link
            </Typography>
          );
        },
      },
      {
        field: 'reportDownload',
        headerText: 'Download',
        width: 92,
        minWidth: 80,
        maxWidth: 112,
        clipMode: 'EllipsisWithTooltip',
        headerTemplate: () => <span title='Download'>Download</span>,
        template: (row: ReportGridRow) => {
          const downloadKind = getDownloadKind(row.contentType);
          return row.reportXmlUrl ? (
            <MuiLink
              href={row.reportXmlUrl}
              target='_blank'
              rel='noreferrer'
              underline='hover'
              sx={{
                color: appColors.actions.accentOutline.text,
                '&:hover': {
                  color: appColors.actions.accentOutline.hoverText,
                },
              }}
              title={`Baixar ${getDownloadLabel(downloadKind)}`}
              aria-label={`Baixar ${getDownloadLabel(downloadKind)}`}
            >
              <DownloadTypeIndicator kind={downloadKind} />
            </MuiLink>
          ) : (
            <Typography variant='body2' color='text.secondary'>
              -
            </Typography>
          );
        },
      },
    ],
    [appColors.actions.accentOutline.hoverText, appColors.actions.accentOutline.text, showAssetColumn],
  );

  const indicatorColumns = useMemo(
    () =>
      visibleDefinitions.map((indicator) => ({
        field: `indicator_${indicator.id}`,
        headerText: indicator.isSentiment ? formatSentimentHeader(indicator.label) : indicator.label,
        headerTextAlign: indicator.isSentiment ? 'Center' : 'Left',
        headerTemplate: () => (
          <span title={indicator.label}>
            {indicator.isSentiment ? formatSentimentHeader(indicator.label) : indicator.label}
          </span>
        ),
        textAlign: indicator.isSentiment ? 'Center' : 'Left',
        width: indicator.isSentiment ? 86 : indicator.id === 'resumo_geral' ? 500 : 112,
        minWidth: indicator.isSentiment ? 72 : indicator.id === 'resumo_geral' ? 360 : 86,
        maxWidth: indicator.isSentiment ? 110 : indicator.id === 'resumo_geral' ? 680 : 148,
        clipMode: indicator.isSentiment ? 'Clip' : 'EllipsisWithTooltip',
        template: (row: ReportGridRow) => {
          const fieldValue = renderIndicatorValue(row, indicator);
          if (indicator.isSentiment) {
            return <SentimentBadge value={fieldValue} />;
          }
          if (typeof fieldValue === 'string') {
            return (
              <Box
                component='span'
                title={fieldValue}
                sx={{
                  whiteSpace: 'normal',
                  wordBreak: 'break-word',
                  display: '-webkit-box',
                  WebkitBoxOrient: 'vertical',
                  WebkitLineClamp: indicator.id === 'resumo_geral' ? 4 : 3,
                  overflow: 'hidden',
                  lineHeight: 1.35,
                }}
              >
                {fieldValue}
              </Box>
            );
          }
          return <Box component='span'>{fieldValue ?? '-'}</Box>;
        },
      })),
    [visibleDefinitions],
  );

  useEffect(() => {
    if (gridRef.current) {
      gridRef.current.refresh();
    }
  }, [gridRows, indicatorColumns]);

  const presetLabel = preset === 'custom' ? 'Seleção personalizada' : PRESET_LABELS[preset];
  const indicatorSummary = useMemo(() => {
    if (selectedIndicators.length === 0) {
      return 'Nenhum indicador';
    }
    const baseIndicators = ['resumo_geral', 'sentimento_geral'];
    const labels: string[] = [];
    if (selectedIndicators.includes('resumo_geral')) {
      labels.push('Resumo geral');
    }
    if (selectedIndicators.includes('sentimento_geral')) {
      labels.push('Sentimento geral');
    }
    const extraCount = selectedIndicators.filter((id) => !baseIndicators.includes(id)).length;

    if (labels.length === 0) {
      return `${selectedIndicators.length} indicadores`;
    }
    if (labels.length === 1) {
      return extraCount > 0 ? `${labels[0]} e mais ${extraCount} indicadores` : labels[0];
    }
    return extraCount > 0
      ? `${labels[0]}, ${labels[1]} e mais ${extraCount} indicadores`
      : `${labels[0]} e ${labels[1]}`;
  }, [selectedIndicators]);
  const reportTypeSummary = useMemo(() => {
    if (reportTypeFilter.length === 0) {
      return 'Todos';
    }
    if (reportTypeFilter.length <= 2) {
      return reportTypeFilter.join(', ');
    }
    return `${reportTypeFilter.slice(0, 2).join(', ')} e mais ${reportTypeFilter.length - 2}`;
  }, [reportTypeFilter]);

  const handlePreset = (nextPreset: string) => {
    setPreset(nextPreset);
    setSelectedIndicators(PRESETS[nextPreset] ?? []);
  };

  const toggleIndicator = (indicatorId: string) => {
    setSelectedIndicators((current) => {
      const exists = current.includes(indicatorId);
      const next = exists ? current.filter((id) => id !== indicatorId) : [...current, indicatorId];
      setPreset(inferPresetFromIndicators(next));
      return next;
    });
  };

  const sentimentCellSx = (tone: string) => {
    if (tone === 'positive') {
      return { bgcolor: alpha(appColors.semantic.success, 0.25) };
    }
    if (tone === 'negative') {
      return { bgcolor: alpha(appColors.semantic.danger, 0.25) };
    }
    if (tone === 'neutral') {
      return { bgcolor: alpha(appColors.semantic.info, 0.2) };
    }
    return { bgcolor: alpha(appColors.semantic.warning, 0.2) };
  };

  const outlineButtonSx = {
    textTransform: 'none',
    fontWeight: 700,
    color: appColors.actions.accentOutline.text,
    borderColor: appColors.actions.accentOutline.border,
    '&:hover': {
      color: appColors.actions.accentOutline.hoverText,
      borderColor: appColors.actions.accentOutline.hoverBorder,
      bgcolor: appColors.actions.accentOutline.hoverBg,
    },
  } as const;

  const chipButtonSx = (selected: boolean) =>
    selected
      ? {
          textTransform: 'none',
          fontSize: 12,
          fontWeight: 700,
          bgcolor: appColors.actions.range.selectedBg,
          borderColor: appColors.actions.range.selectedBorder,
          color: appColors.actions.range.selectedText,
          '&:hover': {
            bgcolor: appColors.actions.range.selectedHoverBg,
            borderColor: appColors.actions.range.selectedHoverBorder,
          },
        }
      : {
          textTransform: 'none',
          fontSize: 12,
          fontWeight: 600,
          color: appColors.actions.range.unselectedText,
          borderColor: appColors.actions.range.unselectedBorder,
          '&:hover': {
            color: appColors.actions.range.unselectedHoverText,
            borderColor: appColors.actions.range.unselectedHoverBorder,
            bgcolor: appColors.actions.range.unselectedHoverBg,
          },
        };

  return (
    <Box sx={{ mt: 0 }}>
      <Paper
        variant='outlined'
        sx={{
          borderRadius: GRID_RADIUS,
          boxShadow: 3,
          position: { md: 'sticky' },
          top: { md: 16 },
          zIndex: { md: 30 },
        }}
      >
        <Box
          sx={{
            borderRadius: GRID_RADIUS,
            overflow: 'hidden',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              columnGap: 1.25,
              rowGap: 0.5,
              px: { xs: 2, md: 2.5 },
              py: 1.25,
              bgcolor: alpha(theme.palette.primary.main, 0.035),
              borderBottom: 1,
              borderColor: 'divider',
            }}
          >
            <Typography variant='subtitle2' fontWeight={700}>
              {presetLabel}
            </Typography>
            <Typography variant='caption' color='text.disabled'>
              •
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {indicatorSummary}
            </Typography>
            <Typography variant='caption' color='text.disabled'>
              •
            </Typography>
            <Typography variant='body2' color='text.secondary'>
              {reportTypeSummary}
            </Typography>
            <Typography variant='caption' color='text.secondary' sx={{ ml: { sm: 'auto' }, whiteSpace: 'nowrap' }}>
              {sortedReports.length} relatórios
            </Typography>
          </Box>
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: 1,
              px: { xs: 2, md: 2.5 },
              py: 1.5,
            }}
          >
            <Button variant='outlined' onClick={() => setIndicatorsOpen(true)} sx={outlineButtonSx}>
              Modos de analise e indicadores
            </Button>
            <Button variant='outlined' onClick={() => setReportTypesOpen(true)} sx={outlineButtonSx}>
              Tipos de relatorio
            </Button>
            {showDateRange ? (
              <Stack direction='row' gap={1} flexWrap='wrap' sx={{ flex: '1 1 330px' }}>
                <TextField
                  size='small'
                  label='Data inicial'
                  type='date'
                  value={reportStartDate}
                  onChange={(event) => updateReportDate('reportStartDate', event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 152, flex: '1 1 152px' }}
                />
                <TextField
                  size='small'
                  label='Data final'
                  type='date'
                  value={reportEndDate}
                  onChange={(event) => updateReportDate('reportEndDate', event.target.value)}
                  InputLabelProps={{ shrink: true }}
                  sx={{ minWidth: 152, flex: '1 1 152px' }}
                />
              </Stack>
            ) : null}
            {allowPortfolioSort ? (
              <FormControl size='small' sx={{ minWidth: 218, flex: '0 1 250px' }}>
                <InputLabel id='portfolio-report-order-label'>Ordenar por</InputLabel>
                <Select
                  labelId='portfolio-report-order-label'
                  label='Ordenar por'
                  value={reportSort}
                  onChange={(event) => setReportSort(event.target.value as InstrumentReportSort)}
                >
                  <MenuItem value='date_desc'>Data: mais recentes</MenuItem>
                  <MenuItem value='portfolio_exposure_desc'>Exposição: maior peso</MenuItem>
                </Select>
              </FormControl>
            ) : null}
          </Box>
        </Box>
      </Paper>

      <Dialog open={isIndicatorsOpen} onClose={() => setIndicatorsOpen(false)} fullWidth maxWidth='md'>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction='row' alignItems='center' justifyContent='space-between'>
            <Typography variant='h6' fontWeight={700}>
              Modos de analise e indicadores
            </Typography>
            <IconButton onClick={() => setIndicatorsOpen(false)} aria-label='Fechar'>
              <X size={18} />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 3 }}>
          <Stack spacing={3}>
            <Box>
              <Typography variant='subtitle2' fontWeight={700}>
                Como funciona
              </Typography>
              <Typography variant='body2' color='text.secondary'>
                Modos de analise sao combinacoes prontas de indicadores. Voce pode escolher um modo ou selecionar
                indicadores manualmente.
              </Typography>
            </Box>

            <Box>
              <Typography variant='caption' sx={{ textTransform: 'uppercase' }} color='text.secondary'>
                Modos de analise
              </Typography>
              <Stack direction='row' flexWrap='wrap' gap={1} sx={{ mt: 1 }}>
                {PRESET_ORDER.map((key) => (
                  <Button
                    key={key}
                    size='small'
                    variant={preset === key ? 'contained' : 'outlined'}
                    onClick={() => handlePreset(key)}
                    sx={chipButtonSx(preset === key)}
                  >
                    {key === 'custom' ? 'Selecao personalizada' : PRESET_LABELS[key]}
                  </Button>
                ))}
              </Stack>
              <Typography variant='caption' color='text.secondary' sx={{ mt: 1 }}>
                Modo sugerido: {PRESET_LABELS[suggestedPreset]}
                {preset === 'custom' ? ' • Selecao personalizada' : ''}
              </Typography>
            </Box>

            <Box>
              <Typography variant='caption' sx={{ textTransform: 'uppercase' }} color='text.secondary'>
                Indicadores
              </Typography>
              <Stack direction='row' flexWrap='wrap' gap={1} sx={{ mt: 1 }}>
                {groupedIndicators.groups.map((group) =>
                  group.items.map((indicator) => {
                    const active = selectedIndicators.includes(indicator.id);
                    return (
                      <Button
                        key={indicator.id}
                        size='small'
                        variant={active ? 'contained' : 'outlined'}
                        onClick={() => toggleIndicator(indicator.id)}
                        sx={chipButtonSx(active)}
                      >
                        {indicator.label}
                      </Button>
                    );
                  }),
                )}
                {groupedIndicators.others.map((indicator) => {
                  const active = selectedIndicators.includes(indicator.id);
                  return (
                    <Button
                      key={indicator.id}
                      size='small'
                      variant={active ? 'contained' : 'outlined'}
                      onClick={() => toggleIndicator(indicator.id)}
                      sx={chipButtonSx(active)}
                    >
                      {indicator.label}
                    </Button>
                  );
                })}
              </Stack>
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog open={isReportTypesOpen} onClose={() => setReportTypesOpen(false)} fullWidth maxWidth='sm'>
        <DialogTitle sx={{ pb: 1 }}>
          <Stack direction='row' alignItems='center' justifyContent='space-between'>
            <Typography variant='h6' fontWeight={700}>
              Tipos de relatorio
            </Typography>
            <IconButton onClick={() => setReportTypesOpen(false)}>
              <X size={18} />
            </IconButton>
          </Stack>
        </DialogTitle>
        <DialogContent sx={{ pt: 1, pb: 3 }}>
          <Stack spacing={2}>
            <Typography variant='body2' color='text.secondary'>
              Escolha quais tipos aparecem na tabela. A selecao e aplicada imediatamente.
            </Typography>
            <Stack direction='row' flexWrap='wrap' gap={1}>
              {reportTypeOptions.map((type) => {
                const checked = reportTypeFilter.includes(type);
                return (
                  <Button
                    key={type}
                    size='small'
                    variant={checked ? 'contained' : 'outlined'}
                    onClick={() => toggleReportType(type)}
                    sx={chipButtonSx(checked)}
                  >
                    {type}
                  </Button>
                );
              })}
            </Stack>
            {reportTypeOptions.length === 0 ? (
              <Typography variant='caption' color='text.secondary'>
                Sem tipos disponiveis.
              </Typography>
            ) : null}
            <Box sx={{ textAlign: 'right' }}>
              <Button
                size='small'
                onClick={() => {
                  reportTypeTouched.current = true;
                  setReportTypeFilter([]);
                }}
                sx={{
                  ...outlineButtonSx,
                  textTransform: 'uppercase',
                  fontSize: 11,
                }}
              >
                Limpar tipos
              </Button>
            </Box>
          </Stack>
        </DialogContent>
      </Dialog>

      {filteredReports.length === 0 ? (
        <Paper
          variant='outlined'
          sx={{
            mt: 3,
            p: 2,
            borderStyle: 'dashed',
            color: 'text.secondary',
            borderRadius: GRID_RADIUS,
          }}
        >
          Nenhum relatório encontrado com os filtros selecionados.
        </Paper>
      ) : (
        <Box
          ref={gridContainerRef}
          sx={{
            mt: 2,
            minHeight: 0,
            ...(isDesktop
              ? {
                  height: gridHeight ?? 'auto',
                  overflow: 'auto',
                  border: '1px solid hsl(var(--border))',
                  borderRadius: GRID_RADIUS,
                  backgroundColor: 'hsl(var(--card))',
                  p: 0,
                }
              : {}),
          }}
        >
          <Box sx={{ display: { xs: 'block', md: 'none' } }}>
            {sortedReports.map((report) => {
              const downloadKind = getDownloadKind(report.contentType);
              const descriptionText = report.documentDescriptionLabel || '-';
              return (
                <Paper key={report.id} variant='outlined' sx={{ p: 2, mb: 2, borderRadius: GRID_RADIUS }}>
                  <Stack direction='row' justifyContent='space-between' alignItems='center'>
                    <Typography variant='subtitle2' fontWeight={700}>
                      {report.deliveryDateLabel}
                    </Typography>
                    <Typography
                      variant='caption'
                      sx={{
                        textTransform: 'uppercase',
                        letterSpacing: 0.6,
                        color: 'text.secondary',
                      }}
                    >
                      {report.documentTypeLabel}
                    </Typography>
                  </Stack>
                  {showAssetColumn && report.assetCode ? (
                    <MuiLink
                      href={`/ativo/${encodeURIComponent(report.assetCode)}`}
                      underline='hover'
                      fontWeight={700}
                      sx={{ mt: 1, display: 'inline-block' }}
                    >
                      {report.assetCode}
                    </MuiLink>
                  ) : null}
                  <Typography variant='body2' sx={{ mt: 1 }}>
                    {descriptionText}
                  </Typography>
                  <Stack direction='row' spacing={2} sx={{ mt: 1, flexWrap: 'wrap' }}>
                    {report.reportUrl ? (
                      <MuiLink
                        underline='hover'
                        href={report.reportUrl}
                        target='_blank'
                        rel='noreferrer'
                        title={typeLabelWithDescription(report.documentTypeLabel, report.documentDescriptionLabel)}
                        aria-label={`Abrir ${report.documentTypeLabel || 'relatório'}`}
                        sx={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 1,
                          color: appColors.actions.accentOutline.text,
                          '&:hover': {
                            color: appColors.actions.accentOutline.hoverText,
                          },
                        }}
                      >
                        {report.documentTypeLabel || 'Abrir'}
                      </MuiLink>
                    ) : (
                      <Typography variant='body2' color='text.secondary'>
                        Sem link
                      </Typography>
                    )}
                    {report.reportXmlUrl ? (
                      <MuiLink
                        underline='hover'
                        href={report.reportXmlUrl}
                        target='_blank'
                        rel='noreferrer'
                        sx={{
                          color: appColors.actions.accentOutline.text,
                          '&:hover': {
                            color: appColors.actions.accentOutline.hoverText,
                          },
                        }}
                      >
                        <DownloadTypeIndicator kind={downloadKind} />
                      </MuiLink>
                    ) : null}
                  </Stack>
                  {visibleDefinitions.length > 0 ? (
                    <Box sx={{ mt: 2, display: 'grid', gap: 1 }}>
                      {visibleDefinitions.map((indicator) => {
                        const fieldValue = renderIndicatorValue(report, indicator);
                        const tone = sentimentTone(fieldValue);
                        return (
                          <Box
                            key={`${report.id}-${indicator.id}`}
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              gap: 2,
                              borderRadius: GRID_RADIUS,
                              border: '1px solid hsl(var(--border))',
                              px: 2,
                              py: 1,
                              fontSize: 12,
                              ...(indicator.isSentiment ? sentimentCellSx(tone) : {}),
                            }}
                          >
                            <Typography variant='body2' fontWeight={600}>
                              {indicator.isSentiment ? formatSentimentHeader(indicator.label) : indicator.label}
                            </Typography>
                            {indicator.isSentiment ? (
                              <SentimentBadge value={fieldValue} />
                            ) : (
                              <Typography variant='body2'>{fieldValue ?? '-'}</Typography>
                            )}
                          </Box>
                        );
                      })}
                    </Box>
                  ) : null}
                </Paper>
              );
            })}
          </Box>

          <Box sx={{ display: { xs: 'none', md: 'block' }, height: '100%' }}>
            <GridComponent
              key={gridKey}
              ref={gridRef}
              dataSource={gridRows}
              width='100%'
              height={isDesktop ? '100%' : 'auto'}
              cssClass='report-grid'
              allowTextWrap
              textWrapSettings={{ wrapMode: 'Content' }}
              enableStickyHeader
              allowResizing
              resizeSettings={{ mode: 'Auto' }}
            >
              <ColumnsDirective>
                {baseColumns.map((column) => (
                  <ColumnDirective key={column.field} {...column} />
                ))}
                {indicatorColumns.map((column) => (
                  <ColumnDirective key={column.field} {...column} />
                ))}
              </ColumnsDirective>
              <Inject services={[Resize]} />
            </GridComponent>
          </Box>
        </Box>
      )}
    </Box>
  );
}
